import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  updateProfile,
  signInWithPopup,
  setPersistence,
  browserSessionPersistence
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  async function ensureSessionPersistence() {
    await setPersistence(auth, browserSessionPersistence);
  }

  async function signup(email, password, displayName) {
    await ensureSessionPersistence();
    const result = await createUserWithEmailAndPassword(auth, email, password);
    
    // Update profile with display name
    await updateProfile(result.user, { displayName });
    
    // Create user document in Firestore
    await setDoc(doc(db, 'users', result.user.uid), {
      uid: result.user.uid,
      email: email,
      displayName: displayName,
      points: 0,
      role: 'user',
      createdAt: new Date(),
      totalQuizzesTaken: 0,
      totalRatingsReceived: 0,
      averageRating: 0
    });
    
    return result;
  }

  function login(email, password) {
    return ensureSessionPersistence().then(() => signInWithEmailAndPassword(auth, email, password));
  }

  async function loginWithGoogle() {
    try {
      await ensureSessionPersistence();
      console.log('Attempting Google login with popup...');
      const result = await signInWithPopup(auth, googleProvider);
      console.log('Google login successful:', result);
      
      // Check if user document exists, if not create it
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      if (!userDoc.exists()) {
        console.log('Creating new user document for Google user');
        await setDoc(doc(db, 'users', result.user.uid), {
          uid: result.user.uid,
          email: result.user.email,
          displayName: result.user.displayName,
          points: 0,
          role: 'user',
          createdAt: new Date(),
          totalQuizzesTaken: 0,
          totalRatingsReceived: 0,
          averageRating: 0
        });
      }
      
      return result;
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  }



  function logout() {
    return signOut(auth);
  }

  async function getUserData(uid) {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      return userDoc.data();
    }
    return null;
  }

  useEffect(() => {
    console.log('Setting up auth state listener...');
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user ? user.email : 'No user');
      console.log('User object:', user);
      try {
        if (user) {
          console.log('User is authenticated, loading additional data...');
          const userData = await getUserData(user.uid);
          console.log('User data from Firestore:', userData);
          // Create a merged object but preserve the original Firebase Auth user methods
          const mergedUser = {
            ...user,
            ...userData,
            // Preserve original Firebase Auth methods
            getIdToken: user.getIdToken,
            reload: user.reload,
            toJSON: user.toJSON
          };
          console.log('Merged user:', mergedUser);
          setCurrentUser(mergedUser);
        } else {
          console.log('No user authenticated, setting currentUser to null');
          setCurrentUser(null);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        setCurrentUser(user); // Set user without additional data if Firestore fails
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    signup,
    login,
    loginWithGoogle,
    logout,
    getUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 